/**
 * A signalling mechanism for Playwright tests.
 *
 * This acts as a bridge between the storybook application and playwright for
 * checking behaviours which cannot be asserted on via the DOM or are
 * otherwise not reflected in application state.
 *
 * This is the 'emit' side of the story. It should be used by
 * production-facing code to emit a signal.
 */

declare global {
	interface Window {
		playwrightSignals: {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			signalsByName: Record<string, any[]>;
			allSignals: string[];
		};
	}
}

const isProduction = (): boolean => process.env.NODE_ENV === 'production';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const registerSignal = (signal: string, meta: any) => {
	window.playwrightSignals.allSignals.push(signal);

	const signalsByName = window.playwrightSignals.signalsByName;
	if (signalsByName[signal]) {
		signalsByName[signal].push(meta);
	} else {
		signalsByName[signal] = [meta];
	}
};

const initPlaywrightSignals = () => {
	window.playwrightSignals = {
		signalsByName: {},
		allSignals: [],
	};
};

/**
 * Emit a signal, along with any metadata about it.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const emitSignal = (signal: string, meta: any = {}): void => {
	if (isProduction()) {
		return;
	}
	if (!window.playwrightSignals) {
		initPlaywrightSignals();
	}
	registerSignal(signal, meta);
};
